import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import type { IMenu } from '@/types/menu';
import { consoleMenus } from '../console.menus';

/**
 * Menu state for the Superadmin Console sidebar/header. Mirrors the CMS
 * `useMenu` hook but drives off `consoleMenus` and drops permission gating —
 * the console is superadmin-only, so every item is always visible.
 */
const useConsoleMenu = () => {
  const location = useLocation();

  const [currentRoute, setCurrentRoute] = useState<IMenu | undefined>();
  const [accordion, setAccordion] = useState<string | undefined>();
  const [currentChildMenu, setCurrentChildMenu] = useState<
    string | undefined
  >();
  const [collapseSidebar, setCollapseSidebar] = useState<boolean>(true);

  const findActiveMenu = useCallback(
    (
      items: IMenu[],
      path: string,
      parent?: IMenu
    ): { matched?: IMenu; parent?: IMenu } => {
      for (const item of items) {
        if (item.children) {
          const found = findActiveMenu(item.children, path, item);
          if (found.matched) return found;
        }

        if (item.route && path.startsWith(item.route)) {
          return { matched: item, parent };
        }
      }
      return {};
    },
    []
  );

  const isMenuActive = (menu: IMenu) => {
    if (menu.children) {
      return !!menu.children.find(child => child.label === currentChildMenu);
    }
    return location.pathname.startsWith(menu.route ?? '');
  };

  useEffect(() => {
    const { parent, matched } = findActiveMenu(consoleMenus, location.pathname);

    setCurrentRoute(matched);

    if (parent) {
      setAccordion(parent.label);
      setCurrentChildMenu(matched?.label);
    } else {
      setAccordion(matched?.label);
      setCurrentChildMenu(undefined);
    }
  }, [location.pathname, findActiveMenu]);

  return {
    currentRoute,
    accordion,
    setAccordion,
    currentChildMenu,
    collapseSidebar,
    setCollapseSidebar,
    isMenuActive,
  };
};

export default useConsoleMenu;
